// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xngGaPLPEvent from "@guestsupply/App/Handlers/GoogleAnalytics/PLP/PLPEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { ProductListFilterAccordionSectionStyles } from "@guestsupply/Overrides/Widgets/ProductList/ProductListFilterAccordionSection";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

export interface ProductListAttributeFiltersStyles extends ProductListFilterAccordionSectionStyles {
    wrapper?: InjectableCss;
}

const mapStateToProps = (state: CustomAppState) => ({
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    // xng-change: add google event
    xngGaPLPEvent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const productListAttributeFiltersStyles: ProductListAttributeFiltersStyles = {};
// XNG-Changes
const styles = productListAttributeFiltersStyles;

const ProductListAttributeFilters: FC<Props> = ({
    attributeTypeFacets,
    addProductFilters,
    removeProductFilters,
    fields,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaPLPEvent,
}) => {
    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 10;
    // XNG-Changes
    const [expanded, setExpanded] = React.useState(false);

    if (!attributeTypeFacets?.length) {
        return null;
    }

    // XNG-Changes
    const limitedAttributes = expanded ? attributeTypeFacets : attributeTypeFacets?.slice(0, showMoreLimit);

    const onChangeFacet = (facet: FacetModel, filterTitle: string) => {
        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.id] });
        } else {
            addProductFilters({ attributeValueIds: [facet.id] });
        }
        // xng-change: add google event
        xngGaPLPEvent({
            plpEventType: GOOGLE_EVENTS.PLP_FILTER,
            includePLPEvent: xGoogleAnalytics.includePLPFilterEvent,
            clickText: facet.name,
            attribute: filterTitle,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {limitedAttributes.map(a =>
                a.attributeValueFacets ? (
                    <ProductListFiltersAccordionSection
                        key={a.attributeTypeId}
                        title={a.nameDisplay}
                        facets={a.attributeValueFacets.map<FacetModel>(av => ({
                            ...av,
                            id: av.attributeValueId,
                            name: av.valueDisplay,
                        }))}
                        onChangeFacet={facet => onChangeFacet(facet, a.nameDisplay)}
                        showMoreLimit={showMoreLimit}
                        expandByDefault={fields.expandByDefault}
                        extendedStyles={productListCategoryFiltersStyles}
                    />
                ) : null,
            )}
            {attributeTypeFacets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)} aria-label="View Less">
                        {translate("View Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)} aria-label="View More">
                        {translate("View More")}
                    </Link>
                ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListAttributeFilters),
    definition: {
        group: "Product List",
        displayName: "Attribute Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
